import React, { useEffect, useState } from 'react';
import Icon from '../AppIcon';

export const DENSITA = ['compatta', 'normale', 'spaziosa'];

const inputClasse = 'w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all';

/**
 * Chips dei filtri attivi, con "x" per rimuoverli.
 * definizioni: [{ key, label, tipo, opzioni? }]; filtri: [{ col, val }]
 */
export const FiltriBadge = ({ filtri, definizioni, onRimuovi, onAzzera }) => {
  const attivi = filtri.map((f, indice) => ({ ...f, indice })).filter((f) => f.col && f.val);
  if (attivi.length === 0) return null;

  const testoValore = (def, val) => {
    if (def?.tipo === 'select') return def.opzioni?.find((o) => o.value === val)?.label ?? val;
    if (def?.tipo === 'data') {
      const [da, a] = String(val).split('|');
      const fmt = (d) => (d ? d.split('-').reverse().join('/') : '');
      if (da && a) return `${fmt(da)} → ${fmt(a)}`;
      return da ? `dal ${fmt(da)}` : `fino al ${fmt(a)}`;
    }
    return val;
  };

  return (
    <div className="flex flex-wrap gap-2 px-1">
      {attivi.map((f) => {
        const def = definizioni.find((d) => d.key === f.col);
        return (
          <div key={f.indice} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <Icon name="Filter" size={10} />
            <span className="text-[10px] font-black uppercase tracking-widest">
              {def?.label ?? f.col}: {testoValore(def, f.val)}
            </span>
            <button
              type="button"
              onClick={() => onRimuovi(f.indice)}
              aria-label="Rimuovi filtro"
              className="ml-1 w-4 h-4 rounded-full bg-primary/20 hover:bg-primary/40 flex items-center justify-center transition-all"
            >
              <Icon name="X" size={9} />
            </button>
          </div>
        );
      })}
      {onAzzera && (
        <button
          type="button"
          onClick={onAzzera}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 text-red-500/70 hover:text-red-500 transition-all text-[10px] font-black uppercase tracking-widest"
        >
          <Icon name="FilterX" size={10} />
          Azzera filtri
        </button>
      )}
    </div>
  );
};

/**
 * Pannello "Impostazioni" della tabella con tre schede: Colonne | Filtri | Densità.
 *
 * colonne          [{ key, label }]  colonne di cui si può cambiare la visibilità
 * colVisibili      { [key]: boolean } (mancante = visibile)
 * filtri           [{ col, val }]    fino a maxFiltri
 * definizioniFiltri [{ key, label, tipo: 'select'|'testo'|'data', opzioni?: [{ value, label }] }]
 *                   (tipo 'data': il valore del filtro è la stringa "da|a" con date YYYY-MM-DD)
 */
const ImpostazioniTabella = ({
  colonne,
  colVisibili,
  setColVisibili,
  densita,
  setDensita,
  filtri,
  setFiltri,
  definizioniFiltri,
  maxFiltri = 5,
  onClose,
}) => {
  const [tab, setTab] = useState('colonne');

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const aggiungiFiltro = () => {
    if (filtri.length >= maxFiltri) return;
    setFiltri((prec) => [...prec, { col: '', val: '' }]);
  };
  const rimuoviFiltro = (i) => setFiltri((prec) => prec.filter((_, idx) => idx !== i));
  const aggiornaFiltro = (i, campo, valore) => {
    setFiltri((prec) => prec.map((f, idx) => (
      idx === i ? { ...f, [campo]: valore, ...(campo === 'col' ? { val: '' } : {}) } : f
    )));
  };

  const attiviN = filtri.filter((f) => f.col && f.val).length;
  const colonneUsate = new Set(filtri.map((f) => f.col).filter(Boolean));

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="relative w-full max-w-sm bg-card border-2 border-border/60 rounded-3xl shadow-2xl animate-in zoom-in-95 fade-in duration-300 overflow-hidden flex flex-col max-h-[85dvh]">
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/60 to-transparent shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border/40 shrink-0">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50 mb-0.5">Pannello</p>
            <h3 className="text-lg font-black uppercase tracking-tight">Impostazioni</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="w-9 h-9 rounded-xl bg-muted/60 hover:bg-red-500/10 hover:text-red-500 border border-border/40 flex items-center justify-center transition-all text-muted-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Schede */}
        <div className="flex shrink-0 border-b border-border/40 px-2 pt-2">
          {[
            { id: 'colonne', label: 'Colonne', icona: 'Columns' },
            { id: 'filtri', label: `Filtri${attiviN > 0 ? ` (${attiviN})` : ''}`, icona: 'Filter' },
            { id: 'densita', label: 'Densità', icona: 'AlignJustify' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 text-[10px] font-black uppercase tracking-widest rounded-t-xl transition-all duration-200 border-b-2 ${
                tab === t.id
                  ? 'text-primary border-primary bg-primary/5'
                  : 'text-muted-foreground/50 border-transparent hover:text-muted-foreground'
              }`}
            >
              <Icon name={t.icona} size={11} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Contenuto */}
        <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin' }}>
          {tab === 'colonne' && (
            <div className="flex flex-col gap-2">
              {colonne.map((col) => {
                const visibile = colVisibili[col.key] !== false;
                return (
                  <label key={col.key} className="flex items-center justify-between cursor-pointer group">
                    <span className="text-sm font-bold text-foreground/70 group-hover:text-foreground transition-colors">{col.label}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={visibile}
                      onClick={() => setColVisibili((prec) => ({ ...prec, [col.key]: !visibile }))}
                      className={`relative w-10 h-5 rounded-full transition-all duration-300 ${visibile ? 'bg-primary' : 'bg-muted/80 border border-border/60'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-300 ${visibile ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </label>
                );
              })}
            </div>
          )}

          {tab === 'filtri' && (
            <div className="flex flex-col gap-4">
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50">
                Aggiungi fino a {maxFiltri} filtri per colonna e valore.
              </p>

              {filtri.map((f, i) => {
                const def = definizioniFiltri.find((d) => d.key === f.col);
                return (
                  <div key={i} className="flex flex-col gap-2.5 p-4 rounded-2xl bg-muted/20 border border-border/30">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-black uppercase tracking-widest text-primary/60">Filtro {i + 1}</span>
                      <button
                        type="button"
                        onClick={() => rimuoviFiltro(i)}
                        aria-label="Rimuovi filtro"
                        className="w-6 h-6 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all"
                      >
                        <Icon name="X" size={11} />
                      </button>
                    </div>

                    <select value={f.col} onChange={(e) => aggiornaFiltro(i, 'col', e.target.value)} className={inputClasse}>
                      <option value="">— Seleziona colonna —</option>
                      {definizioniFiltri.map((d) => (
                        <option key={d.key} value={d.key} disabled={colonneUsate.has(d.key) && d.key !== f.col}>
                          {d.label}
                        </option>
                      ))}
                    </select>

                    {def && def.tipo === 'select' && (
                      <select value={f.val} onChange={(e) => aggiornaFiltro(i, 'val', e.target.value)} className={inputClasse}>
                        <option value="">— Tutti —</option>
                        {(def.opzioni || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    )}

                    {def && def.tipo === 'data' && (() => {
                      const [da = '', a = ''] = String(f.val).split('|');
                      const imposta = (nuovoDa, nuovoA) => aggiornaFiltro(i, 'val', nuovoDa || nuovoA ? `${nuovoDa}|${nuovoA}` : '');
                      return (
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex flex-col gap-1">
                            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/50">Dal</span>
                            <input type="date" value={da} onChange={(e) => imposta(e.target.value, a)} className={inputClasse} />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/50">Al</span>
                            <input type="date" value={a} onChange={(e) => imposta(da, e.target.value)} className={inputClasse} />
                          </label>
                        </div>
                      );
                    })()}

                    {def && def.tipo === 'testo' && (
                      <input
                        type="text"
                        value={f.val}
                        onChange={(e) => aggiornaFiltro(i, 'val', e.target.value)}
                        placeholder="Contiene…"
                        className={inputClasse}
                      />
                    )}
                  </div>
                );
              })}

              {filtri.length < maxFiltri && (
                <button
                  type="button"
                  onClick={aggiungiFiltro}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border-2 border-dashed border-border/40 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground/40 hover:text-primary transition-all duration-200 text-[11px] font-black uppercase tracking-widest"
                >
                  <Icon name="Plus" size={14} />
                  Aggiungi filtro
                </button>
              )}

              {filtri.length > 0 && (
                <button
                  type="button"
                  onClick={() => setFiltri([])}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-2xl bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 text-red-500/70 hover:text-red-500 transition-all text-[11px] font-black uppercase tracking-widest"
                >
                  <Icon name="Trash2" size={12} />
                  Azzera tutti i filtri
                </button>
              )}
            </div>
          )}

          {tab === 'densita' && (
            <div className="flex flex-col gap-3">
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50 mb-1">Spaziatura righe</p>
              {DENSITA.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDensita(d)}
                  className={`flex items-center justify-between px-4 py-3 rounded-2xl border text-sm font-black uppercase tracking-wider transition-all duration-200 ${
                    densita === d
                      ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20'
                      : 'bg-muted/30 border-border/40 text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  <span>{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                  {densita === d && <Icon name="Check" size={14} />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-muted/40 border-t border-border/40 flex justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary rounded-xl text-[11px] font-black uppercase tracking-widest transition-all"
          >
            Fatto
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpostazioniTabella;
